import { Core } from "@faire/design-tokens";
import { Category } from "@faire/retailer-visitor-shared/models/Category/Category";
import { CategoryPagePaths } from "@faire/retailer-visitor-shared/paths/CategoryPage";
import { useRetailerRoutingContext } from "@faire/retailer-visitor-shared/RetailerRoutingContextProvider";
import { trackEventOnClick } from "@faire/web/common/trackEvent";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { trackRetailersPillWithHighlightLowMinHover } from "@faire/web-api--source/events/retailers/hover/pillWithHighlightLowMin";
import { trackRetailersFilterPillTileView } from "@faire/web-api--source/events/retailers/view/filterPillTile";
import { IFilterOption } from "@faire/web-api--source/indigofair/data/IFilterOption";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import * as React from "react";
import { useMemo } from "react";
import styled from "styled-components";

interface IProps {
  brandValueFilterSection: IFilterSection | undefined;
  currentCategoryModel?: Category;
  pillLinkUrl?: (filterOption: IFilterOption) => string;
  onClick?: (filter: string) => void;
}

export const FilterPillTile: React.FC<IProps> = ({
  brandValueFilterSection,
  currentCategoryModel,
  onClick,
  pillLinkUrl,
}) => {
  const { isMobile } = useViewport();
  const routingContext = useRetailerRoutingContext();

  const renderFilterPill = (filterOption: IFilterOption, index: number) => {
    if (!filterOption.key) {
      return null;
    }

    let linkURL = pillLinkUrl ? pillLinkUrl(filterOption) : "";
    const brandValueFilterOption = filterOption.key;
    if (currentCategoryModel) {
      linkURL = CategoryPagePaths.index({
        categoryUrlIds: currentCategoryModel.urlIds,
        filterByBrandValueOption: brandValueFilterOption,
        routingContext,
      });
    }

    const trackClick = onClick;
    return (
      <div
        data-test-id={`filter-pill-tile-pill-${filterOption.key}`}
        key={index}
      >
        <PillWrapper
          tabIndex={0}
          to={linkURL}
          onClick={
            trackClick
              ? () => trackClick(brandValueFilterOption)
              : trackEventOnClick(
                  `logged-out-category-page/pill-tile/tile-click/${linkURL}`
                )
          }
          onMouseOver={() =>
            trackRetailersPillWithHighlightLowMinHover(
              brandValueFilterOption,
              "false"
            )
          }
        >
          <Typography maxLines={2}>{filterOption.display_name}</Typography>
        </PillWrapper>
      </div>
    );
  };

  const renderSeeAllLink = () => {
    if (!isFiltered) {
      return null;
    }
    let linkURL = "";
    if (currentCategoryModel) {
      linkURL = CategoryPagePaths.index({
        categoryUrlIds: currentCategoryModel.urlIds,
        routingContext,
      });
    }

    return (
      <SeeAllRow data-test-id="filter-pill-tile-see-all" key="see-all">
        <Button variant="tertiary" linkTo={linkURL}>
          <StrictLocalMsg defaultMessage="See All Brands" />
        </Button>
        <Spacer size={4} />
        <Arrow direction="right" />
      </SeeAllRow>
    );
  };

  const isFiltered = useMemo(() => {
    return !!(brandValueFilterSection?.options ?? []).find(
      (option) => option.is_selected
    );
  }, [brandValueFilterSection?.options]);

  const filterOptionsToDisplay = useMemo(() => {
    const numFilters = 6;
    return (
      brandValueFilterSection?.options
        .filter((option) => !option.is_selected)
        .slice(0, isFiltered ? numFilters - 1 : numFilters) ?? []
    );
  }, [isFiltered, brandValueFilterSection?.options]);

  const viewRef = useDoActionWhenVisible(() => {
    trackRetailersFilterPillTileView();
  });

  if (!brandValueFilterSection || !filterOptionsToDisplay.length) {
    return null;
  }

  const items = filterOptionsToDisplay.map(renderFilterPill);
  const seeAll = renderSeeAllLink();
  if (seeAll && !isMobile) {
    items.push(seeAll);
  }

  return (
    <Wrapper ref={viewRef}>
      <StyledHeader data-test-id="filter-pill-tile-header">
        <StrictLocalMsg
          description={{
            text: "The title above filter options on category page",
          }}
          defaultMessage="Shop by brand values"
        />
      </StyledHeader>
      <Spacer size="2x" />
      <Column gap="1x">
        {items.map((chunk, column) => (
          <div key={column}>{chunk}</div>
        ))}
      </Column>
    </Wrapper>
  );
};

const Wrapper = styled(Column)`
  padding: ${getSpacing("1x", 0, "1x", "2x")};
  ${responsiveMediaQueries.mobile} {
    padding-left: 0;
    > h4 {
      padding-left: ${getSpacing("1x")};
    }
  }
`;

const PillWrapper = styled(Link)`
  display: flex;
  padding: ${getSpacing(9, "2x")};
  border-radius: 100px;
  border: 1px solid ${Core.border.muted};
  width: fit-content;
  max-width: 100%;

  cursor: pointer;
  &:hover,
  &:focus,
  &:active {
    color: ${Core.text.inverse};
    background-color: ${Core.surface.hover};
    border-color: ${Core.border.hover};
    > p {
      color: ${Core.text.inverse};
    }
  }
  > p {
    word-break: break-word;
  }
  white-space: nowrap;
`;

const SeeAllRow = styled(Row).attrs({
  align: "center",
})`
  padding: ${getSpacing(18, "1x")};
  ${responsiveMediaQueries.mobile} {
    padding-top: ${getSpacing(18)};
  }
`;

const StyledHeader = styled(Typography).attrs({
  forwardedAs: "h4",
  variant: "pageHeaderSerifRegular",
})`
  &&& {
    font-weight: 300;
  }
`;
